import React, { useEffect, useState } from "react";
import serviceApi from "../../../services/serviceApi";
import environment from "../../../environments/environment";

type Summary = {
    workingDays: string;
    presentDays: string;
    absentDays: string;
    percentage: number;
};

type Monthly = {
    workingDays: string;
    presentDays: string;
    absentDays: string;
    month: string;
    percentage: number;
};

const Attendance = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [overallPercentage, setOverallPercentage] = useState<number>(0);
    const [stats, setStats] = useState<any[]>([]);
    const [monthlyData, setMonthlyData] = useState<Monthly[]>([]);
    const [activeFilter, setActiveFilter] = useState<string>("present");

    const fetchAttendance = async () => {
        try {
            setIsLoading(true);

            const res: any = await serviceApi.get(
                environment.attendanceSummary
            );

            const data = res;

            if (data?.objectData) {
                const summary: Summary = data.objectData.summary;

                setOverallPercentage(summary?.percentage || 0);

                setStats([
                    {
                        label: "Working",
                        key: "working",
                        count: summary?.workingDays,
                    },
                    {
                        label: "Present",
                        key: "present",
                        count: summary?.presentDays,
                    },
                    {
                        label: "Absent",
                        key: "absent",
                        count: summary?.absentDays,
                    },
                ]);

                setMonthlyData(data.objectData.monthlyAttendance || []);
            }
        } catch (err) {
            console.error("Attendance API error:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAttendance();
    }, []);

    const getGradient = (percent: number) =>
        `conic-gradient(var(--colorSuccess) 0% ${percent}%, #dee2e6 ${percent}% 100%)`;

    return (
        <div className="attendance-container">
            <h2 className="title-text notification-heading">Attendance</h2>
            {isLoading ? (
                <div className="loading-overlay">
                    <div className="loading-overlay__content">
                        <div className="spinner spinner--lg"></div>
                        <span className="loading-text">
                            Loading attendance...
                        </span>
                    </div>
                </div>
            ) : (
                <>
                    {/* TOP CARD */}
                    <div className="attendance-summary-card">

                        <div className="attendance-circle-wrapper">
                            <div
                                className="attendance-circle"
                                style={{
                                    background: getGradient(overallPercentage),
                                }}
                            >
                                <div className="attendance-circle-inner">
                                    {overallPercentage}%
                                </div>
                            </div>
                        </div>

                        {/* STATS */}
                        <div className="attendance-stats">
                            {stats.map((item) => (
                                <div
                                    key={item.key}
                                    className={`attendance-stat-item ${activeFilter === item.key ? "active" : ""
                                        }`}
                                    onClick={() => setActiveFilter(item.key)}
                                >
                                    {item.label}: {item.count}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* MONTHLY LIST */}
                    <div className="attendance-month-list">
                        {monthlyData.map((month, index) => (
                            <div key={index} className="attendance-month-card">

                                <div className="attendance-month-circle">
                                    <div
                                        className="attendance-circle-small"
                                        style={{
                                            background: getGradient(month.percentage),
                                        }}
                                    >
                                        <div className="attendance-circle-inner-small">
                                            {month.percentage}%
                                        </div>
                                    </div>
                                </div>

                                <div className="attendance-month-info">
                                    <div className="month-name"
                                        style={{ color: "var(--colorAccentDark)" }}>
                                        {month.month}
                                    </div>

                                    <div className="month-stats">
                                        <span>
                                            <span
                                                style={{
                                                    color:
                                                        activeFilter === "working"
                                                            ? "orange"
                                                            : "#999",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                W:
                                            </span>{" "}
                                            {month.workingDays}
                                        </span>

                                        <span>
                                            <span
                                                style={{
                                                    color:
                                                        activeFilter === "present"
                                                            ? "green"
                                                            : "#999",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                P:
                                            </span>{" "}
                                            {month.presentDays}
                                        </span>

                                        <span>
                                            <span
                                                style={{
                                                    color:
                                                        activeFilter === "absent"
                                                            ? "red"
                                                            : "#999",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                A:
                                            </span>{" "}
                                            {month.absentDays}
                                        </span>
                                    </div>
                                </div>

                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default Attendance;